import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';
import { useUnsavedChanges } from '../hooks/useUnsavedChanges';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmationDialog from '../components/ConfirmationDialog';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api';

// Loading skeleton component
const LocationCardSkeleton = () => (
  <div className="border border-light-border dark:border-border rounded-xl p-4 animate-pulse">
    <div className="space-y-2">
      <div className="h-5 bg-slate-200 dark:bg-silver/20 rounded w-64"></div>
      <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-32"></div>
      <div className="h-3 bg-slate-200 dark:bg-silver/20 rounded w-24"></div>
    </div>
  </div>
);

const DoctorLocations = () => {
  const { user, loading: authLoading, isAuthenticated, token } = useAuth();
  const navigate = useNavigate();

  // Locations state
  const [locations, setLocations] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  // Doctor ID state
  const [doctorId, setDoctorId] = useState(null);

  // Form state for adding/editing location
  const [showForm, setShowForm] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [formData, setFormData] = useState({
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom'
  });
  const [formError, setFormError] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Delete state
  const [deletingLocationId, setDeletingLocationId] = useState(null);
  const deletingRef = useRef(null);

  // Delete confirmation dialog state
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    isOpen: false,
    locationId: null,
    locationAddress: ''
  });

  // Initial form values for comparison
  const initialFormValues = useMemo(() => ({
    address: '',
    city: '',
    postcode: '',
    country: 'United Kingdom'
  }), []);

  // Check if form has unsaved changes
  const hasUnsavedChanges = useMemo(() => {
    if (!showForm) return false;
    const compareValues = editingLocation || initialFormValues;
    return (
      formData.address !== (compareValues.address || '') ||
      formData.city !== (compareValues.city || '') ||
      formData.postcode !== (compareValues.postcode || '') ||
      formData.country !== (compareValues.country || 'United Kingdom')
    );
  }, [showForm, formData, editingLocation, initialFormValues]);

  // Use the unsaved changes hook
  const { isBlocked, confirmNavigation, cancelNavigation, checkNavigation } = useUnsavedChanges(
    hasUnsavedChanges,
    'You have unsaved changes to your location. Are you sure you want to leave without saving?'
  );

  // Handle navigation with unsaved changes check
  const handleNavigation = useCallback((path, e) => {
    if (e) e.preventDefault();
    if (!checkNavigation(path)) {
      navigate(path);
    }
  }, [checkNavigation, navigate]);

  useEffect(() => {
    // Redirect if not authenticated or not a doctor
    if (!authLoading && (!isAuthenticated || user?.role !== 'doctor')) {
      navigate('/login');
    }
  }, [authLoading, isAuthenticated, user, navigate]);

  // Fetch doctor ID
  useEffect(() => {
    const fetchDoctorId = async () => {
      if (!isAuthenticated || user?.role !== 'doctor') return;

      try {
        const response = await fetch(`${API_URL}/auth/me`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });

        if (response.ok) {
          const userData = await response.json();
          if (userData.doctorId) {
            setDoctorId(userData.doctorId);
          }
        }
      } catch (err) {
        console.error('Error fetching doctor ID:', err);
      }
    };

    fetchDoctorId();
  }, [isAuthenticated, user, token]);

  // Fetch locations
  const fetchLocations = useCallback(async () => {
    if (!doctorId) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/locations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch locations');
      }

      const data = await response.json();
      setLocations(data.locations || []);
    } catch (err) {
      console.error('Error fetching locations:', err);
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  }, [doctorId, token]);

  useEffect(() => {
    if (doctorId) {
      fetchLocations();
    }
  }, [doctorId, fetchLocations]);

  // Reset form
  const resetForm = useCallback(() => {
    setFormData({
      address: '',
      city: '',
      postcode: '',
      country: 'United Kingdom'
    });
    setEditingLocation(null);
    setShowForm(false);
    setFormError(null);
  }, []);

  // Handle edit click
  const handleEditClick = (location) => {
    setEditingLocation(location);
    setFormData({
      address: location.address || '',
      city: location.city || '',
      postcode: location.postcode || '',
      country: location.country || 'United Kingdom'
    });
    setShowForm(true);
    setFormError(null);
  };

  // Handle form submission (create or update)
  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);
    setIsSubmitting(true);

    // Validate form
    if (!formData.address.trim()) {
      setFormError('Address is required');
      setIsSubmitting(false);
      return;
    }
    if (!formData.city.trim()) {
      setFormError('City is required');
      setIsSubmitting(false);
      return;
    }
    if (!formData.postcode.trim()) {
      setFormError('Postcode is required');
      setIsSubmitting(false);
      return;
    }

    try {
      const url = editingLocation
        ? `${API_URL}/doctors/${doctorId}/locations/${editingLocation.id}`
        : `${API_URL}/doctors/${doctorId}/locations`;

      const method = editingLocation ? 'PUT' : 'POST';

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          address: formData.address.trim(),
          city: formData.city.trim(),
          postcode: formData.postcode.trim(),
          country: formData.country.trim()
        })
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || `Failed to ${editingLocation ? 'update' : 'create'} location`);
      }

      const savedCity = formData.city.trim();
      resetForm();
      fetchLocations();

      // Show success toast
      toast.success(editingLocation
        ? `Location in ${savedCity} updated successfully!`
        : `Location in ${savedCity} added successfully!`
      );
    } catch (err) {
      console.error('Error saving location:', err);
      setFormError(err.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Open delete confirmation dialog
  const handleDeleteClick = (location) => {
    setDeleteConfirmation({
      isOpen: true,
      locationId: location.id,
      locationAddress: `${location.address}, ${location.city}`
    });
  };

  // Cancel delete confirmation
  const handleDeleteCancel = () => {
    setDeleteConfirmation({
      isOpen: false,
      locationId: null,
      locationAddress: ''
    });
  };

  // Handle location deletion
  const handleDeleteConfirm = async () => {
    const locationId = deleteConfirmation.locationId;
    const locationAddress = deleteConfirmation.locationAddress;

    // Rapid-click protection
    if (deletingRef.current !== null) {
      console.log(`[DELETE] Ignoring rapid click - deletion already in progress`);
      return;
    }

    deletingRef.current = locationId;
    setDeletingLocationId(locationId);

    try {
      const response = await fetch(`${API_URL}/doctors/${doctorId}/locations/${locationId}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || 'Failed to delete location');
      }

      // Remove the location from local state
      setLocations(prev => prev.filter(loc => loc.id !== locationId));
      console.log(`[DELETE] Location ${locationId} deleted successfully`);

      // Close the confirmation dialog
      setDeleteConfirmation({
        isOpen: false,
        locationId: null,
        locationAddress: ''
      });

      // Show success toast
      toast.success(`Location deleted successfully.`);
    } catch (err) {
      console.error('Error deleting location:', err);
      setError(err.message);
      setDeleteConfirmation({
        isOpen: false,
        locationId: null,
        locationAddress: ''
      });
    } finally {
      deletingRef.current = null;
      setDeletingLocationId(null);
    }
  };

  // Show loading during auth check
  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-[calc(100vh-5rem)]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-light-text dark:border-pure-white mx-auto mb-4"></div>
          <p className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60">
            Loading...
          </p>
        </div>
      </div>
    );
  }

  if (!isAuthenticated || user?.role !== 'doctor') {
    return null;
  }

  return (
    <div className="min-h-[calc(100vh-5rem)] px-4 py-8">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="flex justify-between items-start mb-8">
          <div>
            <button
              onClick={(e) => handleNavigation('/doctor/dashboard', e)}
              className="flex items-center gap-2 text-light-text-secondary dark:text-silver/60 hover:text-slate-700 dark:hover:text-silver mb-4 transition-smooth"
              data-testid="back-to-dashboard-btn"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Back to Dashboard
            </button>
            <h1 className="font-display text-4xl italic tracking-tighter leading-tight mb-2">
              Manage Locations
            </h1>
            <p className="font-body text-light-text-secondary dark:text-silver/60">
              Add and manage your practice locations where you see patients
            </p>
          </div>
          <button
            onClick={() => {
              resetForm();
              setShowForm(true);
            }}
            className="btn-primary"
            data-testid="add-location-button"
          >
            + Add Location
          </button>
        </div>

        {/* Error message */}
        {error && (
          <div className="mb-6 p-4 bg-red-500/10 border border-red-500/30 rounded-xl">
            <p className="text-red-400">{error}</p>
          </div>
        )}

        {/* Add/Edit Location Form */}
        {showForm && (
          <div className="mb-8 glass border border-light-border dark:border-border rounded-2xl p-6">
            <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
              {editingLocation ? 'Edit Location' : 'Add New Location'}
            </h2>

            {formError && (
              <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
                <p className="text-red-400 text-sm">{formError}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label htmlFor="location-address" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                  Address *
                </label>
                <input
                  type="text"
                  id="location-address"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="e.g., 123 Harley Street"
                  className="input-field"
                  data-testid="location-address-input"
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <label htmlFor="location-city" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    City *
                  </label>
                  <input
                    type="text"
                    id="location-city"
                    value={formData.city}
                    onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                    placeholder="e.g., London"
                    className="input-field"
                    data-testid="location-city-input"
                  />
                </div>

                <div>
                  <label htmlFor="location-postcode" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Postcode *
                  </label>
                  <input
                    type="text"
                    id="location-postcode"
                    value={formData.postcode}
                    onChange={(e) => setFormData({ ...formData, postcode: e.target.value })}
                    placeholder="e.g., W1G 6AB"
                    className="input-field"
                    data-testid="location-postcode-input"
                  />
                </div>

                <div>
                  <label htmlFor="location-country" className="font-mono text-xs uppercase tracking-widest text-light-text-secondary dark:text-silver/60 block mb-2">
                    Country
                  </label>
                  <input
                    type="text"
                    id="location-country"
                    value={formData.country}
                    onChange={(e) => setFormData({ ...formData, country: e.target.value })}
                    placeholder="United Kingdom"
                    className="input-field"
                    data-testid="location-country-input"
                  />
                </div>
              </div>

              <div className="flex gap-4 pt-4">
                <button
                  type="button"
                  onClick={resetForm}
                  className="flex-1 py-3 px-6 border border-light-border dark:border-border rounded-xl text-slate-600 dark:text-silver hover:bg-slate-100 dark:hover:bg-silver/5 transition-smooth"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="flex-1 btn-primary disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="save-location-button"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center gap-2">
                      <svg className="animate-spin h-4 w-4" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                      </svg>
                      Saving...
                    </span>
                  ) : editingLocation ? 'Update Location' : 'Save Location'}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Locations List */}
        <div className="glass border border-light-border dark:border-border rounded-2xl p-6">
          <h2 className="font-mono text-sm uppercase tracking-widest text-light-text-secondary dark:text-silver/60 mb-4">
            Your Locations
          </h2>

          {isLoading ? (
            <div className="space-y-4">
              <LocationCardSkeleton />
              <LocationCardSkeleton />
            </div>
          ) : locations.length > 0 ? (
            <div className="space-y-4" data-testid="locations-list">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="border border-light-border dark:border-border rounded-xl p-4 hover:border-slate-400 dark:hover:border-white/20 transition-smooth"
                  data-testid={`location-item-${location.id}`}
                >
                  <div className="flex justify-between items-start">
                    <div className="flex-1">
                      <h3 className="font-body font-medium text-light-text dark:text-pure-white">
                        {location.address}
                      </h3>
                      <p className="font-body text-sm text-light-text-secondary dark:text-silver/60 mt-1">
                        {location.city}, {location.postcode}
                      </p>
                      <p className="font-mono text-xs text-slate-400 dark:text-silver/40 mt-1">
                        {location.country}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleEditClick(location)}
                        className="text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border border-blue-500/30 text-blue-400 hover:bg-blue-500/10 hover:border-blue-500/50 transition-smooth"
                        data-testid={`edit-location-${location.id}`}
                      >
                        Edit
                      </button>
                      <button
                        onClick={() => handleDeleteClick(location)}
                        disabled={deletingLocationId !== null}
                        className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-lg border transition-smooth ${
                          deletingLocationId === location.id
                            ? 'border-red-500/50 text-red-400 bg-red-500/10 cursor-not-allowed'
                            : 'border-red-500/30 text-red-400 hover:bg-red-500/10 hover:border-red-500/50'
                        } ${deletingLocationId !== null && deletingLocationId !== location.id ? 'opacity-50 cursor-not-allowed' : ''}`}
                        data-testid={`delete-location-${location.id}`}
                      >
                        {deletingLocationId === location.id ? (
                          <span className="flex items-center gap-1">
                            <svg className="animate-spin h-3 w-3" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                            </svg>
                            Deleting...
                          </span>
                        ) : 'Delete'}
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12" data-testid="no-locations-message">
              <svg className="w-16 h-16 text-silver/20 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
              <p className="font-body text-light-text-secondary dark:text-silver/60 mb-2">
                No locations added yet
              </p>
              <p className="font-body text-sm text-slate-400 dark:text-silver/40 mb-6">
                Add your practice locations to help patients find you
              </p>
              <button
                onClick={() => setShowForm(true)}
                className="btn-primary"
              >
                + Add Your First Location
              </button>
            </div>
          )}
        </div>

        {/* Help Section */}
        <div className="mt-8 p-6 bg-blue-500/10 border border-blue-500/30 rounded-2xl">
          <h3 className="font-mono text-sm uppercase tracking-widest text-blue-400 mb-2">
            Pro Tip
          </h3>
          <p className="font-body text-sm text-slate-600 dark:text-silver/80">
            Adding multiple locations helps patients find you in the marketplace.
            Your city will appear in search filters, making it easier for local patients
            to discover your services.
          </p>
        </div>
      </div>

      {/* Unsaved Changes Warning Dialog */}
      <UnsavedChangesDialog
        isOpen={isBlocked}
        onConfirm={confirmNavigation}
        onCancel={cancelNavigation}
        message="You have unsaved changes to your location. Are you sure you want to leave without saving?"
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={deleteConfirmation.isOpen}
        onConfirm={handleDeleteConfirm}
        onCancel={handleDeleteCancel}
        title="Delete Location"
        message={`Are you sure you want to delete "${deleteConfirmation.locationAddress}"? This action cannot be undone.`}
        confirmText="Delete Location"
        cancelText="Cancel"
        isLoading={deletingLocationId === deleteConfirmation.locationId}
        variant="danger"
      />
    </div>
  );
};

export default DoctorLocations;
